import { useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SignIn, SignUp } from "@clerk/react";
import { useAuth as useClerkAuth } from "@clerk/react";
import { useTheme } from "next-themes";
import { useAuth } from "../context/AuthContext";
import { Loader2 } from "lucide-react";
import Logo from "../components/Logo";
import { getClerkAppearance } from "../lib/clerkAppearance";

export default function AuthPage() {
  const { user } = useAuth();
  const { isLoaded: clerkLoaded } = useClerkAuth();
  const { resolvedTheme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const clerkAppearance = useMemo(
    () => getClerkAppearance(resolvedTheme === "light" ? "light" : "dark"),
    [resolvedTheme],
  );

  const mode = searchParams.get("mode") === "register" ? "register" : "login";
  const nextPath = searchParams.get("next") || "/lobby";

  useEffect(() => {
    if (user && user !== false) {
      navigate(nextPath.startsWith("/") ? nextPath : "/lobby", { replace: true });
    }
  }, [user, navigate, nextPath]);

  const setMode = (nextMode) => {
    const params = new URLSearchParams(searchParams);
    if (nextMode === "register") params.set("mode", "register");
    else params.delete("mode");
    setSearchParams(params, { replace: true });
  };

  if (!clerkLoaded || user === null) {
    return (
      <div className="min-h-screen bg-tribal grid place-items-center">
        <Loader2 className="animate-spin text-[color:var(--text-secondary)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-tribal grid place-items-center px-6 py-12">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="block text-center mb-8" data-testid="auth-back-home">
          <Logo size="lg" className="mx-auto" />
        </Link>

        <div className="glass rounded-lg p-6 sm:p-8">
          <div className="flex bg-[color:var(--surface)] rounded-md p-1 mb-6">
            <button
              type="button"
              onClick={() => setMode("login")}
              data-testid="auth-tab-login"
              className={`flex-1 py-2 text-sm rounded transition ${mode === "login" ? "btn-gold" : "text-[color:var(--text-secondary)]"}`}
            >
              Connexion
            </button>
            <button
              type="button"
              onClick={() => setMode("register")}
              data-testid="auth-tab-register"
              className={`flex-1 py-2 text-sm rounded transition ${mode === "register" ? "btn-gold" : "text-[color:var(--text-secondary)]"}`}
            >
              Inscription
            </button>
          </div>

          <div className="clerk-auth-panel flex justify-center" data-testid="clerk-auth-panel">
            {mode === "login" ? (
              <SignIn
                routing="virtual"
                signUpUrl="/auth?mode=register"
                fallbackRedirectUrl={nextPath}
                forceRedirectUrl={nextPath}
                appearance={clerkAppearance}
              />
            ) : (
              <SignUp
                routing="virtual"
                signInUrl="/auth"
                fallbackRedirectUrl={nextPath}
                forceRedirectUrl={nextPath}
                appearance={clerkAppearance}
              />
            )}
          </div>

          <p className="mt-6 text-center text-xs text-[color:var(--text-secondary)]">
            En continuant, vous acceptez les règles du Perē Fa&apos;anu&apos;u et notre{" "}
            <Link to="/politique-confidentialite" className="text-[color:var(--primary)] hover:underline">
              politique de confidentialité
            </Link>
            .
          </p>
        </div>

        <Link
          to="/"
          className="block text-center mt-6 text-xs text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)] transition"
          data-testid="auth-back-link"
        >
          ← Retour à l&apos;accueil
        </Link>
      </div>
    </div>
  );
}
